import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '../../../Layouts/Shared/_PublicLayout';

/* ─── Design tokens — referans sayfayla birebir ─── */
const T = {
  navy:   '#0a2540',
  blue:   '#0050d8',
  blueLt: '#e8f0fe',
  border: '#d9dde3',
  bg:     '#f5f7fa',
  muted:  '#4a5568',
  light:  '#6b7a8d',
};

const ROUTES = { home: '/' };
const COMMON  = 'public.static.static-pages.common';

/* ─────────────────────────────────────────────
   TENANT DEFAULT DEĞERLERİ
   Prop gelmezse → dil dosyası denenir (t('tenant.x'))
   O da boşsa → buradaki sabit değer kullanılır.
───────────────────────────────────────────── */
const TENANT_DEFAULTS = {
  brandName:            'Tatalli Cloud',
  brandNameFromOwn:     'Şirketten',
  brandNameToOwn:       'Şirkete',
  tenantBrandName:      'Tatalli',
  companyName:          'Tatalli Teknoloji ve Ticaret Ltd. Şti.',
  companyAddress:       'ÜNALAN MAH. LİBADİYE CAD. EMAR RESIDENCE BLOK NO: 82E İÇ KAPI NO: 291 ÜSKÜDAR / İSTANBUL',
  companyTradingNumber: '435859-5',
  companyEmail:         'info@tatalli.com.tr',
  companyPhone:         '+90 (850) 309 2950',
  companyKep:           'tatalli@hs01.kep.tr',
  companyMobileApp:     'Tatalli',
  webSiteWithWww:       'cloud.tatalli.com.tr',
  webSiteMobileApp:     'Tatalli Cloud Mobil',
};

/* Prop → dil dosyası → sabit default sırasıyla çözümler */
function useTenant(props) {
  const { t } = useTranslation();
  const keys = {
    brandName:            'tenant.company-brand-name',
    brandNameFromOwn:     'tenant.company-brand-name-from-own',
    brandNameToOwn:       'tenant.company-brand-name-to-own',
    tenantBrandName:      'tenant.brand-name',
    companyName:          'tenant.company-name',
    companyAddress:       'tenant.company-address',
    companyTradingNumber: 'tenant.company-trading-number',
    companyEmail:         'tenant.company-email-address',
    companyPhone:         'tenant.company-phone-number',
    companyKep:           'tenant.company-kep-address',
    companyMobileApp:     'tenant.company-mobile-app-name',
    webSiteWithWww:       'tenant.web-site-with-www',
    webSiteMobileApp:     'tenant.web-site-mobile-app-name',
  };
  const resolved = {};
  Object.entries(keys).forEach(([field, i18nKey]) => {
    if (props[field]) {
      resolved[field] = props[field];
    } else {
      const fromI18n = t(i18nKey);
      resolved[field] = (fromI18n && fromI18n !== i18nKey)
        ? fromI18n
        : TENANT_DEFAULTS[field];
    }
  });
  return resolved;
}

/* Breadcrumb — referans sayfayla birebir */
function Breadcrumb({ label, color }) {
  const { t } = useTranslation();
  const c = color || T.light;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 13, color: c }}>
      <Link to={ROUTES.home} style={{ color: c, textDecoration: 'none' }}>
        {t(`${COMMON}.breadcrumb_home`)}
      </Link>
      <i className="fas fa-chevron-right" style={{ fontSize: 10 }} />
      <span>{label}</span>
    </div>
  );
}

/* ──────────────────────────────────────────────────
   İÇ YARDIMCI BİLEŞENLER
   (shell dışında kalan, sadece bu sayfaya özel)
────────────────────────────────────────────────── */

/* Uyarı / bilgi bandı */
function CalloutBox({ variant, children }) {
  const map = {
    info:    { bg: T.blueLt,    border: T.blue,    color: '#1e3a8a' },
    warn:    { bg: '#fffbeb',   border: '#f59e0b', color: '#78350f' },
    danger:  { bg: '#fff1f2',   border: '#e11d48', color: '#881337' },
    success: { bg: '#f0fdf4',   border: '#16a34a', color: '#14532d' },
  };
  const s = map[variant] || map.info;
  return (
    <div style={{
      background: s.bg,
      borderLeft: `4px solid ${s.border}`,
      borderRadius: '0 6px 6px 0',
      padding: '12px 16px',
      margin: '14px 0',
      color: s.color,
      fontSize: 13.5,
      lineHeight: 1.7,
    }}>
      {children}
    </div>
  );
}

/* Bölüm ayraç şeridi */
function SectionDivider({ id, num, title, subtitle }) {
  return (
    <div
      id={id}
      style={{
        borderLeft: `4px solid ${T.blue}`,
        background: T.blueLt,
        borderRadius: '0 8px 8px 0',
        padding: '16px 20px',
        marginTop: 44,
        marginBottom: 24,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: T.blue, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
        Bölüm {num}
      </div>
      <div style={{ fontWeight: 700, fontSize: 17, color: T.navy }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12.5, color: T.light, marginTop: 4 }}>{subtitle}</div>}
    </div>
  );
}

/* Madde başlığı */
function ArticleTitle({ children }) {
  return (
    <p style={{
      fontWeight: 700,
      color: T.navy,
      fontSize: 13.5,
      borderBottom: `1px solid ${T.border}`,
      paddingBottom: 5,
      marginTop: 24,
      marginBottom: 10,
      letterSpacing: '0.01em',
    }}>
      {children}
    </p>
  );
}

/* İçindekiler */
const SECTIONS = [
  { id: 'uyelik',   num: '01', label: 'Üyelik Sözleşmesi'         },
  { id: 'mesafeli', num: '02', label: 'Mesafeli Satış Sözleşmesi' },
  { id: 'iptal',    num: '03', label: 'İptal & İade Politikası'   },
  { id: 'teslimat', num: '04', label: 'Teslimat & Aktivasyon'     },
];

function TableOfContents() {
  return (
    <nav style={{
      background: '#fff',
      border: `1px solid ${T.border}`,
      borderRadius: 8,
      padding: '16px 20px',
      marginBottom: 36,
    }}>
      <div style={{ fontWeight: 700, color: T.navy, fontSize: 13, marginBottom: 12 }}>
        Bu Sayfada
      </div>
      <ol style={{ margin: 0, paddingLeft: 20 }}>
        {SECTIONS.map(s => (
          <li key={s.id} style={{ marginBottom: 6 }}>
            <a href={`#${s.id}`} style={{ color: T.blue, textDecoration: 'none', fontSize: 13.5 }}>
              {s.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* İade matrisi satırı */
function RefundRow({ service, refundable, note, isHeader }) {
  if (isHeader) {
    return (
      <div style={{
        display: 'grid', gridTemplateColumns: '2fr 1fr 3fr', gap: 8,
        padding: '9px 14px', background: T.navy, color: '#fff',
        fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
        borderRadius: '6px 6px 0 0',
      }}>
        <span>Hizmet Türü</span><span>İade</span><span>Koşul / Açıklama</span>
      </div>
    );
  }
  const isYes = refundable === 'Evet';
  const isNo  = refundable === 'Hayır';
  const tagColor = isYes ? '#14532d' : isNo ? '#881337' : '#78350f';
  const tagBg    = isYes ? '#f0fdf4'  : isNo ? '#fff1f2'  : '#fffbeb';
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '2fr 1fr 3fr', gap: 8,
      padding: '10px 14px', borderBottom: `1px solid ${T.border}`,
      background: '#fff', fontSize: 13, alignItems: 'start',
    }}>
      <span style={{ color: T.navy, fontWeight: 500 }}>{service}</span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: tagBg, color: tagColor,
        fontSize: 11, fontWeight: 700,
        padding: '2px 8px', borderRadius: 4, width: 'fit-content',
      }}>
        {isYes ? '✓' : isNo ? '✗' : '~'} {refundable}
      </span>
      <span style={{ color: T.muted, lineHeight: 1.65 }}>{note}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════
   ANA SAYFA — shell referans sayfayla birebir
══════════════════════════════════════════════ */
export default function AgreementsPage(props) {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.agreements';
  const tenant = useTenant(props);

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        .ppd-hero { background: ${T.bg}; border-bottom: 1px solid ${T.border}; padding: 36px 0; }
        .ppd-body { padding: 36px 0; }
        .ppd-content { max-width: 860px; font-size: 14px; color: ${T.muted}; line-height: 1.8; }
        .ppd-content h2 { font-size: 20px; font-weight: 700; color: ${T.blue}; margin: 32px 0 12px; }
        .ppd-content p { margin-bottom: 12px; }
        .ppd-content ul { margin: 8px 0 12px 20px; }
        .ppd-content ol { margin: 8px 0 12px 20px; }
        .ppd-content li { margin-bottom: 6px; }
        .ppd-content strong { color: ${T.navy}; }
        .ppd-content a { color: ${T.blue}; }
        @media (max-width: 768px) { .ppd-hero, .ppd-body { padding: 28px 0; } }
        @media (max-width: 480px) { .ppd-hero, .ppd-body { padding: 20px 0; } }
      `}</style>

      {/* Hero */}
      <div className="ppd-hero">
        <div className="pl-container">
          <Breadcrumb label={t(`${p}.breadcrumb`)} />
          <h1 style={{ fontSize: 'clamp(22px,4vw,32px)', fontWeight: 700, color: T.navy, marginBottom: 12 }}>
            {t(`${p}.hero_title`)}
          </h1>
          <p style={{ fontSize: 15, color: T.muted, margin: 0, maxWidth: 520 }}>
            {t(`${p}.hero_desc`)}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="ppd-body">
        <div className="pl-container">
          <div className="ppd-content">
            <TableOfContents />
            <UyelikSozlesmesi    tenant={tenant} />
            <MesafeliSatis       tenant={tenant} />
            <IptalIade           tenant={tenant} />
            <TeslimatAktivasyon  tenant={tenant} />
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

/* ══════════════════════════════════════════════
   BÖLÜM 1 – ÜYELİK SÖZLEŞMESİ
══════════════════════════════════════════════ */
function UyelikSozlesmesi({ tenant }) {
  const {
    brandName, companyName, companyAddress,
    companyEmail, companyPhone, companyKep, webSiteWithWww,
  } = tenant;

  return (
    <>
      <SectionDivider
        id="uyelik"
        num="01"
        title="Üyelik Sözleşmesi"
        subtitle="Platform'a üye olma, hesap yönetimi ve hizmetlerin kullanım koşulları"
      />

      <p>
        İşbu Üyelik Sözleşmesi (&quot;Sözleşme&quot;), {companyAddress} adresinde mukim{' '}
        <strong>{companyName}</strong> (&quot;{brandName}&quot;) ile Platforma üye olan
        gerçek veya tüzel kişi (&quot;ÜYE&quot;) arasında,{' '}
        <strong>{webSiteWithWww}</strong> üzerinden sunulan bulut hizmetlerinin kullanım
        esas ve koşullarını düzenlemek amacıyla akdedilmiştir.
      </p>

      <ArticleTitle>MADDE 1 – TANIMLAR</ArticleTitle>
      <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
        <li><strong>Platform:</strong> {brandName} tarafından işletilen, bulut hizmetlerinin sunulduğu {webSiteWithWww} alan adlı web arayüzü ve bağlı API&apos;leri ifade eder.</li>
        <li><strong>Hizmetler:</strong> Platform üzerinden sunulan Cloud Instance (sanal makine), IAM (Kimlik ve Erişim Yönetimi), Directory ve benzeri SaaS/IaaS çözümleri ile bunlara ilişkin destek ve yönetim araçlarının tümüdür.</li>
        <li><strong>ÜYE:</strong> Sözleşme&apos;yi kabul ederek Platform&apos;a kayıt olan gerçek veya tüzel kişidir.</li>
        <li><strong>Hesap:</strong> ÜYE&apos;ye özgü kimlik bilgileri ile erişilen, Hizmetlerin yönetildiği kullanıcı ortamıdır.</li>
        <li><strong>SLA:</strong> İlgili hizmete ait ayrıca yayımlanan çalışma süresi ve destek taahhütlerini içeren Hizmet Düzeyi Belgesidir.</li>
      </ul>

      <ArticleTitle>MADDE 2 – SÖZLEŞMENİN KONUSU VE KAPSAMI</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>İşbu Sözleşme, ÜYE&apos;nin Platform üzerinden Hizmetlere erişimine ilişkin hak ve yükümlülükleri düzenler.</li>
        <li>ÜYE, Sözleşme&apos;yi kabul ederek Platform&apos;da yayımlanan Gizlilik Politikası, Çerez Politikası ve Hizmet Düzeyi belgelerini de kabul etmiş sayılır; bu belgeler Sözleşme&apos;nin ayrılmaz eklerini oluşturur.</li>
        <li>{brandName}, yalnızca Hizmetlerin teknik altyapısını sağlar; ÜYE&apos;nin barındırdığı veri, uygulama veya içerikten doğan hukuki sorumluluk münhasıran ÜYE&apos;ye aittir.</li>
      </ul>

      <ArticleTitle>MADDE 3 – TARAFLARIN HAK VE YÜKÜMLÜLÜKLERİ</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>Platform&apos;a üye olabilmek için 18 yaşını doldurmuş olmak veya tüzel kişiliği temsile yetkili olmak gerekir. ÜYE, kayıt sırasında sağladığı bilgilerin doğru ve güncel olduğunu beyan eder.</li>
        <li>Hesap erişim bilgilerinin (kullanıcı adı, şifre, API anahtarı vb.) güvenliğinden ÜYE sorumludur. Yetkisiz erişim durumunda ÜYE derhal {brandName}&apos;i bilgilendirir.</li>
        <li>ÜYE, Hizmetleri yalnızca yasal amaçlarla kullanır; üçüncü kişilerin sistemlerine yetkisiz erişim, DDoS saldırısı, zararlı yazılım barındırma veya yayma gibi faaliyetlerde bulunamaz.</li>
        <li>{brandName}, Hizmetlerin kesintisiz çalışması için gerekli teknik ve idari tedbirleri alır; planlı bakım çalışmalarını önceden duyurur.</li>
        <li>{brandName}, güvenlik açığı, şüpheli kullanım veya mevzuat zorunluluğu halinde önceden bildirim yapmaksızın ilgili Hesabı geçici olarak askıya alma hakkını saklı tutar.</li>
        <li>ÜYE, kullanımına açılan kaynakların yapılandırması ve güvenliğinden kendi sorumludur.</li>
      </ul>

      <ArticleTitle>MADDE 4 – ÜCRETLENDIRME VE ÖDEME</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>Hizmet ücretleri Platform&apos;da yayımlanan güncel fiyat listesine göre belirlenir. {brandName}, fiyatları değiştirme hakkını saklı tutar; değişiklik öncesinde ÜYE bilgilendirilir.</li>
        <li>Ödemeler, Platform&apos;da belirtilen yöntem ve dönemlerle yapılır. Zamanında ödenmeyen tutarlara yasal faiz uygulanabilir.</li>
        <li>Ödeme yapılmaması halinde {brandName}, ilgili Hizmetleri askıya alma veya sonlandırma hakkına sahiptir.</li>
      </ul>

      <ArticleTitle>MADDE 5 – SONA ERME VE TAZMİNAT</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>Taraflardan her biri Sözleşme&apos;yi yazılı bildirimle feshedebilir. Fesih tarihine kadar doğan hak ve borçlar taraflarca karşılıklı ifa edilir.</li>
        <li>ÜYE&apos;nin Sözleşme&apos;ye aykırı davranması halinde {brandName}, tazminat talep etme hakkı saklı kalmak kaydıyla Sözleşme&apos;yi derhal feshedebilir.</li>
        <li>{brandName}, dolaylı, özel veya arızi zararlardan (veri kaybı, gelir kaybı dahil) sorumlu tutulamaz.</li>
      </ul>

      <ArticleTitle>MADDE 6 – DEĞİŞİKLİKLER</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>{brandName}, işbu Sözleşme&apos;yi ve eklerini Platform&apos;da yayımlayarak tek taraflı olarak değiştirebilir. Değişiklikler yayım tarihinde yürürlüğe girer.</li>
        <li>Değişiklik sonrası Hizmetleri kullanmaya devam eden ÜYE, yeni koşulları kabul etmiş sayılır.</li>
      </ul>

      <ArticleTitle>MADDE 7 – MÜCBİR SEBEPLER</ArticleTitle>
      <p>
        Doğal afet, savaş, salgın, siber saldırı, altyapı arızası veya yasal düzenleme gibi
        tarafların kontrolü dışındaki mücbir sebeplerden kaynaklanan ifa imkânsızlığı halinde
        ilgili taraf bu Sözleşme kapsamında sorumlu tutulamaz.
      </p>

      <ArticleTitle>MADDE 8 – FİKRİ MÜLKİYET HAKLARI</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>Platform, Hizmetler ve bunlara ilişkin tüm yazılım, tasarım ve dokümantasyon üzerindeki fikri mülkiyet hakları {brandName}&apos;e aittir.</li>
        <li>ÜYE, Hizmetleri ters mühendislik, kopyalama veya türev ürün oluşturma amacıyla kullanamaz.</li>
        <li>ÜYE&apos;nin bu Sözleşme&apos;den kaynaklanan haklardan başka hiçbir hak {brandName}&apos;e devredilmez.</li>
      </ul>

      <ArticleTitle>MADDE 9 – GİZLİLİK VE KİŞİSEL VERİLER</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>Taraflar, Sözleşme kapsamında edindikleri gizli bilgileri üçüncü kişilerle paylaşmaz; bu yükümlülük Sözleşme sona erdikten sonra da geçerliliğini korur.</li>
        <li>Kişisel verilerin işlenmesi, Gizlilik Politikası ve 6698 sayılı KVKK çerçevesinde yürütülür.</li>
      </ul>

      <ArticleTitle>MADDE 10 – MUHTELİF HÜKÜMLER</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li><strong>Bütünlük ve Bölünebilirlik:</strong> İşbu Sözleşme, Taraflar arasındaki anlaşmanın tamamını oluşturur. Herhangi bir hükmün geçersizliği diğer hükümleri etkilemez.</li>
        <li><strong>Uygulanacak Hukuk ve Yetki:</strong> Sözleşme, Türkiye Cumhuriyeti hukukuna tabidir; uyuşmazlıklarda İstanbul Merkez (Çağlayan) Mahkemeleri ve İcra Müdürlükleri yetkilidir.</li>
        <li><strong>Delil Sözleşmesi:</strong> {brandName}&apos;in elektronik kayıtları ve log dosyaları HMK m. 193 uyarınca kesin delil niteliği taşır.</li>
        <li><strong>Bildirim:</strong> Resmi bildirimler kayıtlı e-posta veya KEP üzerinden yapılır. ÜYE iletişim bilgilerini güncel tutmakla yükümlüdür.</li>
        <li><strong>Devir:</strong> ÜYE, {brandName}&apos;in yazılı onayı olmaksızın bu Sözleşme&apos;den doğan hak ve yükümlülüklerini devredemez.</li>
        <li>
          <strong>İletişim Bilgileri:</strong><br />
          Adres: {companyAddress}<br />
          E-posta: {companyEmail}<br />
          Telefon: {companyPhone}<br />
          KEP: {companyKep}
        </li>
      </ul>
    </>
  );
}

/* ══════════════════════════════════════════════
   BÖLÜM 2 – MESAFELİ SATIŞ SÖZLEŞMESİ
══════════════════════════════════════════════ */
function MesafeliSatis({ tenant }) {
  const {
    brandName, companyName, companyAddress,
    companyEmail, companyPhone, companyKep, webSiteWithWww,
  } = tenant;

  return (
    <>
      <SectionDivider
        id="mesafeli"
        num="02"
        title="Mesafeli Satış Sözleşmesi"
        subtitle="6502 sayılı TKHK ve Mesafeli Sözleşmeler Yönetmeliği (29188 sayılı RG) kapsamında"
      />

      <p>
        İşbu Mesafeli Satış Sözleşmesi, 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve
        Mesafeli Sözleşmeler Yönetmeliği (RG: 27.11.2014/29188) hükümleri çerçevesinde,
        aşağıda bilgileri yer alan SATICI ile Platform üzerinden hizmet satın alan ALICI
        arasında elektronik ortamda kurulmuştur.
      </p>

      <ArticleTitle>MADDE 1 – SATICI BİLGİLERİ</ArticleTitle>
      <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
        <li><strong>Ticaret Unvanı:</strong> {companyName}</li>
        <li><strong>Platform / Marka:</strong> {brandName}</li>
        <li><strong>Adres:</strong> {companyAddress}</li>
        <li><strong>E-posta:</strong> {companyEmail}</li>
        <li><strong>Telefon:</strong> {companyPhone}</li>
        <li><strong>KEP:</strong> {companyKep}</li>
        <li><strong>Web Sitesi:</strong> {webSiteWithWww}</li>
      </ul>

      <ArticleTitle>MADDE 2 – ALICI BİLGİLERİ</ArticleTitle>
      <p>
        ALICI, Platform&apos;a kayıt sırasında sağladığı ad-soyad / ticaret unvanı, adres,
        e-posta ve iletişim bilgileriyle tanımlanır. ALICI, bu bilgilerin doğru ve güncel
        olduğunu beyan eder.
      </p>

      <ArticleTitle>MADDE 3 – SÖZLEŞMENİN KONUSU VE HİZMET TANIMI</ArticleTitle>
      <p>
        İşbu Sözleşme, ALICI&apos;nın <strong>{webSiteWithWww}</strong> adresi üzerinden
        satın aldığı dijital bulut hizmetlerinin (Cloud Instance, IAM, Directory ve benzeri
        SaaS/IaaS ürünleri) satışı ve sunumuna ilişkin karşılıklı hak ve yükümlülükleri
        belirler.
      </p>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>Sunulan hizmetler tamamen dijital niteliktedir; fiziksel teslimat söz konusu değildir.</li>
        <li>Hizmetin kapsamı, özellikleri ve teknik gereksinimleri Platform&apos;daki ürün sayfalarında ayrıntılı olarak açıklanmıştır.</li>
        <li>ALICI, satın alma öncesinde bu bilgileri incelediğini ve onayladığını kabul eder.</li>
      </ul>

      <ArticleTitle>MADDE 4 – FİYAT VE ÖDEME KOŞULLARI</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>Hizmet bedeli, sipariş tamamlama ekranında KDV dahil olarak ALICI&apos;ya gösterilir.</li>
        <li>Ödemeler Platform&apos;da sunulan yöntemlerle (kredi/banka kartı, havale vb.) gerçekleştirilir.</li>
        <li>Abonelik modelinde ücretlendirme, seçilen dönem başlangıcında tahsil edilir.</li>
        <li>ALICI, ödeme bilgilerinin doğruluğundan ve hesap güvenliğinden sorumludur.</li>
        <li>{brandName}, piyasa koşullarına bağlı olarak fiyatları güncelleyebilir; mevcut abonelikler dönem sonuna kadar sabit tutulur.</li>
      </ul>

      <ArticleTitle>MADDE 5 – CAYMA HAKKI</ArticleTitle>
      <CalloutBox variant="info">
        <strong>Önemli:</strong> Cayma hakkı yalnızca tüketici niteliğindeki ALICILAR için
        ve yalnızca belirli hizmet türlerinde geçerlidir. Hizmet türüne göre detaylar
        aşağıda ve İptal &amp; İade Politikası bölümünde açıklanmaktadır.
      </CalloutBox>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>
          Tüketici niteliğindeki ALICI, sipariş tarihinden itibaren <strong>14 (on dört) gün</strong>{' '}
          içinde, ancak yalnızca henüz aktive edilmemiş ve ifasına başlanmamış hizmetler için
          cayma hakkını kullanabilir.
        </li>
        <li>
          6502 sayılı Kanun m. 15/ğ uyarınca; <strong>ALICI&apos;nın talebi ve açık onayıyla
          cayma süresi dolmadan ifasına başlanan dijital içerik ve hizmetlerde cayma hakkı
          kullanılamaz.</strong> {brandName}, bu durumu sipariş öncesinde ALICI&apos;ya açıkça
          bildirmekte ve onayını almaktadır.
        </li>
        <li>
          Alan adı kaydı / transferi, VPS-VDS, kiralık sunucu, SSL sertifikası, co-location,
          lisans ve yazılım hizmetleri; nitelikleri gereği cayma hakkı kapsamı dışındadır ve
          bu hizmetler için herhangi bir koşulda geri ödeme yapılmaz.
        </li>
        <li>
          Cayma talebinin kabulü halinde hizmet derhal durdurulur ve ödeme, 14 gün içinde
          aynı ödeme yöntemiyle iade edilir.
        </li>
      </ul>

      <ArticleTitle>MADDE 6 – UYGULANACAK HUKUK VE UYUŞMAZLIK ÇÖZÜMÜ</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>İşbu Sözleşme Türkiye Cumhuriyeti hukukuna tabidir.</li>
        <li>Tüketici niteliğindeki ALICILAR için ikamet ettikleri veya satışın yapıldığı yerdeki Tüketici Hakem Heyeti (yasal parasal sınırlar dahilinde) ve Tüketici Mahkemeleri yetkilidir.</li>
        <li>Tüketici niteliği taşımayan ALICILAR için İstanbul Merkez (Çağlayan) Mahkemeleri ve İcra Müdürlükleri yetkilidir.</li>
        <li>{brandName}&apos;in elektronik kayıtları ve log dosyaları HMK m. 193 uyarınca kesin delil niteliği taşır.</li>
      </ul>

      <ArticleTitle>MADDE 7 – YÜRÜRLÜK VE ONAY</ArticleTitle>
      <p>
        ALICI, sipariş tamamlama adımında &quot;Sözleşmeyi Okudum ve Kabul Ediyorum&quot;
        onayını vererek işbu Sözleşme&apos;yi elektronik ortamda imzalamış ve bir kopyasını
        edinme hakkına sahip olduğunu teyit etmiş sayılır.
      </p>
    </>
  );
}

/* ══════════════════════════════════════════════
   BÖLÜM 3 – İPTAL & İADE POLİTİKASI
══════════════════════════════════════════════ */
function IptalIade({ tenant }) {
  const { brandName, companyEmail, companyPhone, webSiteWithWww } = tenant;

  const refundRows = [
    { service: 'Cloud Instance (Sanal Makine)',  refundable: 'Koşullu', note: 'Aktivasyon öncesi iptal: tam iade. Aktivasyon sonrası: saatlik kullanım bedeli düşülerek kalan tutar iade edilir.' },
    { service: 'IAM / Directory Hizmeti',        refundable: 'Koşullu', note: 'Hizmet ifasına başlanmadan önce yapılan iptallerde tam iade; ifaya başlandıktan sonra iade yapılmaz.' },
    { service: 'Web Hosting (Shared / Managed)', refundable: 'Evet',    note: 'İlk 30 gün içinde yapılan iptallerde tam iade garantisi uygulanır.' },
    { service: 'Alan Adı Kaydı / Transferi',     refundable: 'Hayır',   note: 'Kayıt veya transfer anında ICANN/kayıt otoritelerine iletildiğinden iptal edilemez ve iade yapılmaz.' },
    { service: 'VPS / VDS Sunucu',               refundable: 'Hayır',   note: 'Kaynaklar sipariş anında tahsis edildiğinden iptal ve iade kabul edilmez.' },
    { service: 'Kiralık (Dedicated) Sunucu',     refundable: 'Hayır',   note: 'Fiziksel donanım tahsisi ve kurulumu başladığından iptal ve iade kabul edilmez.' },
    { service: 'SSL Sertifikası',                refundable: 'Hayır',   note: 'Sertifika otoritesi tarafından düzenlendikten sonra iade yapılmaz.' },
    { service: 'Co-location Hizmeti',            refundable: 'Hayır',   note: 'Fiziksel konum tahsisi ve hazırlık maliyeti doğduğundan iade yapılmaz.' },
    { service: 'Yazılım Lisansı',                refundable: 'Hayır',   note: 'Üretici tarafından aktive edilen lisanslar iade kapsamı dışındadır.' },
    { service: 'Ek Yazılım / SaaS Modülü',       refundable: 'Hayır',   note: 'Dijital ürün niteliği taşıdığından ve ifası anında başladığından iade yapılmaz.' },
  ];

  return (
    <>
      <SectionDivider
        id="iptal"
        num="03"
        title="İptal & İade Politikası"
        subtitle="Hizmet türüne göre iptal koşulları, cayma hakkı ve iade prosedürleri"
      />

      <p>
        {brandName}, şeffaf ve sektör standartlarıyla uyumlu bir iptal ve iade politikası
        uygulamaktadır. Sunulan hizmetlerin dijital niteliği, fiziksel ürünlerden farklı
        yasal çerçeve gerektirdiğinden politika hizmet türüne göre farklılaşmaktadır.
      </p>

      {/* ── İade Matrisi ── */}
      <ArticleTitle>1. HİZMET TÜRÜNE GÖRE İADE TABLOSU</ArticleTitle>
      <CalloutBox variant="warn">
        <strong>Genel Kural:</strong> İade garantisi yalnızca aşağıda
        &quot;Evet&quot; veya &quot;Koşullu&quot; olarak işaretlenen hizmetlere ve
        belirtilen koşullara tabidir. Alan adı, VPS-VDS, kiralık sunucu, SSL sertifikası,
        co-location, lisans ve yazılım hizmetleri iptal edilemez; bu hizmetlere hiçbir
        koşulda geri ödeme yapılmaz.
      </CalloutBox>

      <div style={{ borderRadius: 6, overflow: 'hidden', border: `1px solid ${T.border}`, margin: '12px 0 20px' }}>
        <RefundRow isHeader />
        {refundRows.map((row, i) => (
          <div key={row.service} style={ i === refundRows.length - 1 ? { borderBottom: 'none' } : {} }>
            <RefundRow {...row} />
          </div>
        ))}
      </div>

      {/* ── Cayma Hakkı ── */}
      <ArticleTitle>2. CAYMA HAKKI (TÜKETİCİLER İÇİN)</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>
          Tüketici niteliğindeki aboneler, aboneliği başlattıkları tarihten itibaren{' '}
          <strong>14 gün</strong> içinde, ifasına başlanmamış hizmetler için cayma hakkını
          kullanabilir.
        </li>
        <li>
          Cayma talebi <strong>{companyEmail}</strong> adresine veya Platform destek
          merkezine yazılı olarak iletilmelidir.
        </li>
        <li>
          Cayma bildirimi alındıktan sonra hizmet derhal durdurulur; ödeme 14 gün içinde
          aynı yöntemle iade edilir.
        </li>
      </ul>
      <CalloutBox variant="danger">
        <strong>Cayma Hakkının Kullanılamayacağı Durumlar:</strong> 6502 sayılı Kanun m. 15/ğ
        uyarınca; ALICI&apos;nın talebi ve açık onayıyla, cayma süresi dolmadan ifasına
        başlanmış dijital içerik ve hizmetlerde cayma hakkı kullanılamaz. Alan adı, VPS-VDS,
        kiralık sunucu, SSL, co-location, lisans ve yazılım hizmetleri bu kapsama girmektedir.
      </CalloutBox>

      {/* ── Abonelik İptali ── */}
      <ArticleTitle>3. ABONELİK İPTALİ</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>ALICI, aktif aboneliğini istediği zaman Platform&apos;daki hesap ayarları veya destek kanalı üzerinden iptal edebilir.</li>
        <li>
          <strong>Aylık abonelikler:</strong> İptal talebi, bir sonraki dönemin başlangıcından en az{' '}
          <strong>24 saat</strong> önce iletilmelidir; aksi halde o dönem ücreti tahsil edilir.
          Mevcut dönem için iade yapılmaz.
        </li>
        <li>
          <strong>Yıllık abonelikler:</strong> İlk <strong>30 gün</strong> içinde yapılan
          iptallerde, kullanılan günlere ilişkin ücret hesaplanarak (günlük oran × kullanılan
          gün) kalan tutar iade edilir. 30 günü geçen iptallerde iade yapılmaz.
        </li>
        <li>
          İptal edilen hesaplardaki veriler, iptal tarihinden itibaren <strong>30 gün</strong>{' '}
          boyunca yedekte tutulur; bu süre sonunda kalıcı olarak silinir.
        </li>
      </ul>

      {/* ── İade Prosedürü ── */}
      <ArticleTitle>4. İADE YÖNTEMİ VE SÜRELERİ</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>İadeler yalnızca ödemenin yapıldığı yöntemle gerçekleştirilir.</li>
        <li>Kredi / banka kartı iadeleri: bankanın işlem süresine bağlı olarak <strong>3–14 iş günü</strong> içinde yansır.</li>
        <li>Havale / EFT iadeleri: hesap bilgilerinin teyidinin ardından <strong>3–5 iş günü</strong> içinde tamamlanır.</li>
        <li>Platform kaynaklı teknik arızalarda, SLA&apos;da tanımlı hizmet kredisi iade yerine geçebilir; tercih ALICI&apos;ya bırakılır.</li>
        <li>Promosyonlu satın alımlarda iade tutarı, uygulanan indirim düşülerek hesaplanır.</li>
        <li>Hizmet Şartları&apos;nı ihlal ettiği tespit edilen hesaplara iade yapılmaz.</li>
      </ul>

      {/* ── Talep Kanalları ── */}
      <ArticleTitle>5. İPTAL VE İADE TALEBİ KANALLARI</ArticleTitle>
      <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
        <li style={{ marginBottom: 8 }}>
          <strong>Destek Portalı:</strong>{' '}
          <a href={`https://${webSiteWithWww}`}>{webSiteWithWww}</a>{' '}
          — Hesabınıza giriş yaparak &quot;Destek &gt; Yeni Talep&quot; bölümünden oluşturun.
        </li>
        <li style={{ marginBottom: 8 }}>
          <strong>E-posta:</strong>{' '}
          <a href={`mailto:${companyEmail}`}>{companyEmail}</a>{' '}
          — Konu satırına &quot;İptal / İade Talebi – [Hesap ID]&quot; yazın.
        </li>
        <li>
          <strong>Telefon:</strong> {companyPhone}{' '}
          (09:00–18:00, Pazartesi–Cuma, resmi tatiller hariç)
        </li>
      </ul>
    </>
  );
}

/* ══════════════════════════════════════════════
   BÖLÜM 4 – TESLİMAT & AKTİVASYON
══════════════════════════════════════════════ */
function TeslimatAktivasyon({ tenant }) {
  const { brandName, companyEmail, companyPhone, webSiteWithWww } = tenant;

  const activationRows = [
    ['Cloud Instance (Sanal Makine)',  '≤ 5 dak.',       'Ödeme onayından itibaren otomatik hazırlama'],
    ['IAM (Kimlik ve Erişim Yönetimi)', 'Anlık',         'Hesap oluşturulduğu anda erişim aktif'],
    ['Directory Hizmeti',              '≤ 10 dak.',      'Dizin senkronizasyonu tamamlanarak aktif'],
    ['Web Hosting',                    '≤ 15 dak.',      'Sunucu yapılandırması ve DNS hazırlama'],
    ['SSL Sertifikası',                '≤ 24 saat',      'Sertifika otoritesi onayına bağlı'],
    ['Kiralık Sunucu',                 '1–72 saat',      'Fiziksel donanım hazırlama ve test süreci'],
    ['Enterprise / Özel Yapılandırma', 'Sözleşmeye göre','Satış ekibiyle mutabık kalınan tarihte'],
  ];

  return (
    <>
      <SectionDivider
        id="teslimat"
        num="04"
        title="Teslimat & Aktivasyon"
        subtitle="Dijital hizmet teslimi, aktivasyon süreleri ve erişim koşulları"
      />

      <p>
        {brandName} tarafından sunulan tüm hizmetler <strong>tamamen dijital niteliktedir</strong>.
        Fiziksel ürün veya paket gönderimi yapılmamaktadır. Hizmetin &quot;teslimi&quot;,
        ödeme onayının ardından ALICI&apos;nın Hesabına ilgili kaynağın otomatik olarak
        tanımlanmasıyla gerçekleşir.
      </p>

      <ArticleTitle>1. AKTİVASYON SÜRECİ</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>Ödemenin başarıyla tamamlanmasının ardından hizmet aktivasyonu <strong>otomatik ve anlık</strong> olarak gerçekleştirilir; ek bir onay veya kurulum adımı gerekmez.</li>
        <li>ALICI, {webSiteWithWww} üzerindeki Hesabına giriş yaptığında ilgili kaynakların erişime açık olduğunu görecektir.</li>
        <li>Aktivasyonla birlikte kayıtlı e-posta adresine erişim bilgilerini ve sipariş özetini içeren onay e-postası gönderilir.</li>
      </ul>

      <ArticleTitle>2. AKTİVASYON SÜRELERİ</ArticleTitle>
      <div style={{ borderRadius: 6, overflow: 'hidden', border: `1px solid ${T.border}`, margin: '12px 0 20px' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 8,
          padding: '9px 14px', background: T.navy, color: '#fff',
          fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
          borderRadius: '6px 6px 0 0',
        }}>
          <span>Hizmet</span><span>Süre</span><span>Açıklama</span>
        </div>
        {activationRows.map(([svc, time, desc], i, arr) => (
          <div key={svc} style={{
            display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 8,
            padding: '10px 14px',
            borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none',
            background: '#fff', fontSize: 13, alignItems: 'start',
          }}>
            <span style={{ color: T.navy, fontWeight: 500 }}>{svc}</span>
            <span style={{ color: T.blue, fontWeight: 700 }}>{time}</span>
            <span style={{ color: T.muted }}>{desc}</span>
          </div>
        ))}
      </div>

      <ArticleTitle>3. GECİKME VE SORUN DURUMUNDA</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>
          Belirtilen aktivasyon süresi içinde hizmet açılmadıysa lütfen{' '}
          <strong>{companyEmail}</strong> adresine bildirin veya{' '}
          <strong>{companyPhone}</strong> numaralı destek hattımızı arayın.
        </li>
        <li>
          {brandName} kaynaklı teknik gecikmelerde ödeme iade edilir veya hizmet
          kredilendirilir; tercih ALICI&apos;ya bırakılır.
        </li>
      </ul>

      <ArticleTitle>4. ERİŞİM VE KULLANILABİLİRLİK</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>Hizmetler 7/24 erişilebilir olacak şekilde tasarlanmıştır; hedef çalışma süresi SLA belgesinde tanımlıdır.</li>
        <li>Planlı bakım çalışmaları en az <strong>48 saat</strong> öncesinden Platform ve e-posta yoluyla duyurulur.</li>
        <li>ALICI, hizmetlere erişmek için gereken minimum sistem gereksinimleri ve internet bağlantısından kendi sorumludur.</li>
      </ul>

      <ArticleTitle>5. VERİ SAKLAMA VE SİLME</ArticleTitle>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>
          Abonelik sona erdiğinde ALICI&apos;nın verileri, Hesap kapatma tarihinden itibaren{' '}
          <strong>30 gün</strong> boyunca yedekte tutulur; bu süre sonunda kalıcı olarak silinir.
        </li>
        <li>
          ALICI, aboneliği sonlandırmadan önce kendi verilerini dışa aktarmakla yükümlüdür.{' '}
          {brandName}, bu süre aşıldıktan sonra veri kurtarma taahhüdünde bulunamaz.
        </li>
      </ul>

      <ArticleTitle>6. İLETİŞİM</ArticleTitle>
      <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
        <li style={{ marginBottom: 6 }}><strong>E-posta:</strong> <a href={`mailto:${companyEmail}`}>{companyEmail}</a></li>
        <li style={{ marginBottom: 6 }}><strong>Telefon:</strong> {companyPhone} (09:00–18:00, Pazartesi–Cuma)</li>
        <li><strong>Destek Portalı:</strong> <a href={`https://${webSiteWithWww}`}>{webSiteWithWww}</a></li>
      </ul>
    </>
  );
}